import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { GenericSvg, SVGName } from "../../../assets/svgs/GenericSVG";
import { TextButton } from "../../../core/components/buttons/TextButton";
import { hexWithOpacity } from "../../../core/utils/hexWithOpacity";
import { DocumentCamera } from "../../../features/home/DocumentCamera";
import { Small } from "../../styles/stringStyles";
import { useStyles } from "../../styles/theme";
import { ImageComponent } from "../ImageComponent";
import { ModalContainer } from "../modal/ModalContainer";

type Props = {
  maxMBFileSize?: number;
  handleUpload: (files: FileList) => void;
  file?: File | null | undefined;
  canUploadMultipleFiles?: boolean;
  type: "video" | "image" | "both" | "file";
  initialImageUrl?: string;
  deleteButton?: {
    text: string;
    onclick: () => void;
    disabled?: boolean;
  };
  placeHolderIcon?: SVGName;
  rounded?: boolean;
  editToolParams?: {
    editText: string;
    onEditClick?: () => void;
  };
};

export const MediaUpload = ({
  maxMBFileSize,
  handleUpload,
  file,
  canUploadMultipleFiles,
  type,
  initialImageUrl,
  deleteButton,
  placeHolderIcon,
  rounded,
  editToolParams,
}: Props) => {
  const theme = useStyles();
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | undefined>(
    undefined,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  const onButtonClick = () => {
    inputRef.current?.click();
  };

  const handleCameraClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCamera(true);
  };

  const handleCameraCapture = (imageData: string | undefined) => {
    if (imageData) {
      // Convert base64 to File
      fetch(imageData)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          handleUpload(dataTransfer.files);
          setShowCamera(false);
          setCapturedImage(undefined);
        });
    }
  };

  const getAcceptedFileTypes = () => {
    switch (type) {
      case "image":
        return ".jpg,.jpeg,.png, .heic, .heif";
      case "video":
        return ".mp4,.mov";
      case "both":
        return ".jpg,.jpeg,.png,.mp4,.mov";
      case "file":
        return "application/pdf,.pdf,.jpg,.jpeg,.png,.heic,.heif";
      default:
        return "";
    }
  };

  const getSupportedFormats = () => {
    switch (type) {
      case "image":
        return "JPG, PNG, HEIC, HEIF";
      case "video":
        return "MP4, MOV";
      case "both":
        return "JPG, PNG, MP4, MOV";
      case "file":
        return "PDF, JPG, PNG, HEIC, HEIF";
      default:
        return "";
    }
  };

  const isFileImage = (file: File) => {
    return file?.type?.startsWith("image/");
  };

  const isFileVideo = (file: File) => {
    return file?.type?.startsWith("video/");
  };

  const acceptedFileTypes = getAcceptedFileTypes();
  const supportedFormats = getSupportedFormats();

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (deleteButton?.onclick) {
      deleteButton.onclick();
    }
  };

  const renderFilePreview = () => {
    if (!file) {
      return initialImageUrl ? (
        <LogoImage src={initialImageUrl} alt={`image`} $rounded={rounded} />
      ) : (
        <GenericSvg
          size={100}
          name={placeHolderIcon ?? "Close"}
          primaryColor={theme.colors.white}
          secondaryColor={theme.colors.dark}
        />
      );
    }

    if (isFileImage(file)) {
      return (
        <LogoImage
          src={URL.createObjectURL(file)}
          alt={`Uploaded image`}
          $rounded={rounded}
        />
      );
    } else if (isFileVideo(file)) {
      return (
        <video width="160" height="80" controls>
          <source src={URL.createObjectURL(file)} type={file.type} />
          Your browser does not support the video tag.
        </video>
      );
    }

    return null;
  };

  return (
    <UploadContainer>
      <Subtitle>
        <SmallText>{`${t(
          "MEDIA_UPLOAD_SUPPORTED_FORMATS",
        )} ${supportedFormats}`}</SmallText>
        {maxMBFileSize && (
          <SmallText>{`${t(
            "MEDIA_UPLOAD_MAX_FILE_SIZE",
          )} ${maxMBFileSize}MB`}</SmallText>
        )}
      </Subtitle>
      <DropZone
        $dragActive={dragActive}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={onButtonClick}
      >
        <HiddenInput
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes}
          onChange={handleChange}
          multiple={canUploadMultipleFiles}
        />
        {renderFilePreview()}
        <Text>
          {t("MEDIA_UPLOAD_DRAG_DROP_OR")}
          <TextButton
            text={t("MEDIA_UPLOAD_SELECT_FILE")}
            noPadding
            color={theme.colors.medium}
            onClick={() => {}}
          />
        </Text>
        {(type === "image" || type === "both" || type === "file") &&
          !isMobile && (
            <CameraButtonWrapper>
              <TextButton
                text={t("CAMERA_TAKE_PHOTO")}
                noPadding
                color={theme.colors.medium}
                onClick={handleCameraClick}
              />
            </CameraButtonWrapper>
          )}
        {editToolParams && (
          <TextButton
            text={editToolParams.editText}
            noPadding
            color={theme.colors.medium}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              editToolParams.onEditClick?.();
            }}
          />
        )}
        {canUploadMultipleFiles && (
          <Small>{t("MEDIA_UPLOAD_MULTIPLE_FILES")}</Small>
        )}
      </DropZone>
      {showCamera && (
        <ModalContainer
          handleClose={() => {
            setShowCamera(false);
            setCapturedImage(undefined);
          }}
          // title={t("CAMERA_TAKE_PHOTO") || "Take Photo"}
        >
          <CameraWrapper>
            <DocumentCamera
              image={capturedImage}
              setImage={setCapturedImage}
              remoteImage={undefined}
            />
            {capturedImage && (
              <ConfirmButtonWrapper>
                <TextButton
                  text={t("CONFIRM") || "Confirm"}
                  color={theme.colors.primary}
                  onClick={() => handleCameraCapture(capturedImage)}
                />
              </ConfirmButtonWrapper>
            )}
          </CameraWrapper>
        </ModalContainer>
      )}
      {deleteButton && (
        <DeleteContainer>
          <TextButton
            text={deleteButton?.text}
            color={theme.colors.lightRed}
            onClick={handleDelete}
            disabled={deleteButton.disabled}
          />
        </DeleteContainer>
      )}
    </UploadContainer>
  );
};

const DeleteContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const UploadContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const DropZone = styled.div<{ $dragActive: boolean }>`
  cursor: pointer;
  border: 3px dashed
    ${({ theme, $dragActive }) =>
      $dragActive
        ? theme.colors.primary
        : hexWithOpacity(theme.colors.medium, 0.2)};
  border-radius: 20px;
  padding: 1rem;
  text-align: center;
  background-color: ${({ theme, $dragActive }) =>
    $dragActive
      ? hexWithOpacity(theme.colors.medium, 0.1)
      : theme.colors.white};
  transition: all 0.3s ease;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  &:hover {
    cursor: pointer;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Text = styled.p`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.medium};
`;

const SmallText = styled(Text)`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.medium};
`;

const Subtitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 10px;
  padding-bottom: 5px;
`;

const LogoImage = styled(ImageComponent)<{ $rounded?: boolean }>`
  width: 100px;
  height: 100px;
  border-radius: ${(props) => (props.$rounded ? "50%" : "20px")};
  object-fit: cover;
  box-shadow: 0px 6px 12px 0px rgba(0, 0, 0, 0.2);
`;

const CameraButtonWrapper = styled.div`
  margin-top: 0.5rem;
`;

const CameraWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const ConfirmButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`;
